import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Popover } from '@abyss/web/ui/Popover';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import find from 'lodash/find';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGetRecommendationPreferencesStatus } from '../../../hooks/useGetRecommendationPreferencesStatus';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { useSaveRecommendationPreferences } from '../../../hooks/useSaveRecommendationPreferencesStatus';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { useSmartChoiceSessionStorage } from '../../../hooks/useSmartChoiceSessionStorage';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import {
  SMART_CHOICE_EVENTS,
  dispatchSmartChoiceEvent,
} from '../../../utils/smartChoiceEvents';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import { CareCategories, Constants, SectionType } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { mobileOnly } from '../../ConstantsStyles';
import { LinkContainer } from '../../LinkContainer/LinkContainer';
import {
  coachmarkStyle,
  smartChoiceLinkStyle,
} from '../../PSXHeader/PSXHeader.styled';
import { isValidCareCategoryWithSectionType } from '../../Utils/getProviderType';
import { IntroModal } from './IntroModal';
import { NonSmartChoiceOnboarding } from './NonSmartChoiceOnboarding';
import PopoverContent from './PopoverContent';
import { determineIsSuperUser } from './superUserUtils';
import { formatPreferencePayload } from './utils';
import { OnboardingForm, PreferencesForm } from '.';

type Props = {
  breadcrumbs?: any[];
  isMixedResultsPage?: boolean;
  showMemberSelection?: boolean;
  showChoosePCPHeader?: boolean;
  isFullScreenCompareSmartChoice?: boolean;
  flexCss?: any;
  isParcelWidget?: boolean;
  isSuperUser?: boolean | string;
  memberId?: string;
  submitButtonLabel?: string;
};

export const SmartChoiceWidget = ({
  isFullScreenCompareSmartChoice = false,
  flexCss,
  isParcelWidget = false,
  isSuperUser: isSuperUserProp = false,
  memberId,
  submitButtonLabel,
}: Props) => {
  const { t } = useTranslation();
  const realHsidUuid = useHsidUuid();

  // Use memberId prop if provided, otherwise fallback to realHsidUuid
  const effectiveUserId = memberId || realHsidUuid;
  const allConfigs = useLagoon('config');

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('mapPinRedesign') === 'true') {
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN,
      true
    );
  }

  const [enableSmartChoiceIntroPopup] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_INTRO_POPUP,
  ]);

  const isMobileScreen = useMediaQuery(mobileOnly);

  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [showNonSmartChoiceOnboarding, setShowNonSmartChoiceOnboarding] =
    useState(false);
  const [isPreferencesModalOpen, setPreferencesModalOpen] =
    React.useState(false);
  const enableNonSmartChoiceDisclaimer = useFeatureFlag([
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NON_SMART_CHOICE_DISCLAIMER,
  ])[0];
  const { sectionType, search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
      sectionType: state.typeaheadSearchStore.sectionType, // Extract sectionType
      search: state.typeaheadSearchStore.search, // Extract search
    }))
  );

  const lagoonData = useLagoon('ui-messaging')();

  const SC_COACHMARK = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_COACHMARK,
  });
  const SMART_CHOICE_INTRO_POP_UP_SUBTITLE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_SUBTITLE,
  });
  const SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT,
  });
  const SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER,
  });
  const locationAnalytics = `modal:${SMART_CHOICE_INTRO_POP_UP_SUBTITLE?.message}`;

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const [coachmarkWeb, setCoachmarkWebOpen] = useState<boolean>(false);

  const recommendationPreferencesStatusFromStore = useStore(
    StoreKeys.RECOMMENDATION_PREFERENCES
  );

  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );
  const [showSmartchoiceIntro, setShowSmartchoiceIntro] = useState(false);
  const [showCoachMarkCondition, setShowCoachMarkCondition] = useState(false);
  // State to track if sessionStorage has been initialized with preferences
  // Check on mount if sessionStorage already has values from a previous page
  const [isSessionStorageReady, setIsSessionStorageReady] = useState<
    boolean | undefined
  >(() => {
    const hasDismissedPopover = sessionStorage.getItem('hasDismissedPopover');
    return hasDismissedPopover !== null;
  });

  // Determine isSuperUser with proper order of operations and fallback
  // Priority 0: Use prop from parent (e.g., CEX) if provided
  // Priority 1: Guest check
  // Priority 2: Session storage
  // Priority 3: Lagoon config
  // Priority 4: Fallback to false
  const isSuperUser = determineIsSuperUser(allConfigs, isSuperUserProp);

  const [preferencesData, getPreferenceData] =
    useGetRecommendationPreferencesStatus({});

  useEffect(() => {
    if (!recommendationPreferencesStatusFromStore && isParcelWidget) {
      getPreferenceData({
        variables: {
          ...(effectiveUserId && { userId: effectiveUserId }),
          isSuperUser,
        },
      });
    }
  }, [isSuperUser, isParcelWidget, effectiveUserId]);

  const recommendationPreferencesStatus = useMemo(() => {
    const apiData = preferencesData?.data?.recommendationPreferencesStatus;
    const storeData = recommendationPreferencesStatusFromStore;

    // If we have store data with a lastUpdatedDate, compare to determine which is newer
    // Store data is updated with save responses, so it may be more recent than the initial API fetch
    if (storeData?.lastUpdatedDate && apiData?.lastUpdatedDate) {
      const storeDate = new Date(storeData.lastUpdatedDate).getTime();
      const apiDate = new Date(apiData.lastUpdatedDate).getTime();
      return storeDate >= apiDate ? storeData : apiData;
    }

    // If only store has lastUpdatedDate (after a save), prefer store data
    if (storeData?.lastUpdatedDate) {
      return storeData;
    }

    // Otherwise, prefer API data (initial load) or fallback to store
    return apiData || storeData;
  }, [
    preferencesData?.data?.recommendationPreferencesStatus,
    recommendationPreferencesStatusFromStore,
  ]);

  // Use custom hook to set sessionStorage values before any render that depends on them
  useSmartChoiceSessionStorage({
    preferencesData,
    isSuperUser,
    enableSmartChoiceIntroPopup,
    setRecommendationPreferences,
    setIsSessionStorageReady,
    disabled: !isParcelWidget,
  });

  const [saveResult, saveRecommendationPreferences] =
    useSaveRecommendationPreferences({
      handleSuccess: () => {
        // Refetch preferences after save - this is required for the backend to properly persist
        // the data. The save response alone is not sufficient; the subsequent GET call
        // finalizes the save operation.
        getPreferenceData({
          variables: {
            ...(effectiveUserId && { userId: effectiveUserId }),
            isSuperUser,
          },
        });
        setPreferencesModalOpen(false);
      },
      handleError: (error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to save recommendation preferences:', error);
        // Close the modal even on error to prevent UI getting stuck
        setPreferencesModalOpen(false);
      },
    });

  useEffect(() => {
    const savedPreferences =
      saveResult?.data?.saveRecommendationPreferencesStatus;
    if (savedPreferences) {
      setRecommendationPreferences(savedPreferences);

      // Dispatch event for parent apps (e.g., CEX) to update their Redux store
      if (isParcelWidget) {
        dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.PREFERENCES_SAVED, {
          preferences: savedPreferences,
          source: 'api-save',
          timestamp: new Date().toISOString(),
          userId: effectiveUserId,
          isSuperUser,
        });
      }
    }
  }, [
    saveResult?.data?.saveRecommendationPreferencesStatus,
    isParcelWidget,
    effectiveUserId,
    isSuperUser,
    setRecommendationPreferences,
  ]);

  const isSmartChoiceWithChipValue = useSmartChoice();
  const isSmartChoiceFFEnabled = useSmartChoice(true);

  const isSmartChoice =
    chipValue === CareCategories.ALL
      ? isSmartChoiceFFEnabled
      : isSmartChoiceWithChipValue;

  const shouldDisplayCoachMarkBasedOnChipValue = () =>
    (chipValue === CareCategories.ALL ||
      chipValue === CareCategories.PRIMARY_CARE ||
      chipValue === CareCategories.MEDICAL_SPECIALISTS) &&
    sectionType !== SectionType.PROVIDER_GROUP;

  // Initialize showSmartchoiceIntro and showCoachMarkCondition once sessionStorage is ready
  useEffect(() => {
    if (recommendationPreferencesStatus) {
      const hasDismissedPopover = sessionStorage.getItem('hasDismissedPopover');
      const smartChoicePopoverDismissed = sessionStorage.getItem(
        'smartChoicePopoverDismissed'
      );
      const shouldShowIntro =
        hasDismissedPopover === 'n' && smartChoicePopoverDismissed !== 'true';
      setShowSmartchoiceIntro(shouldShowIntro);

      // Compute coachmark condition
      const hasDismissedCoachMarkWeb =
        recommendationPreferencesStatus?.hasDismissedCoachMarkWeb;
      let shouldShowCoachMark = false;

      if (enableSmartChoiceIntroPopup) {
        // If popover hasn't been dismissed (hasDismissedPopover === 'n'), don't show coachmark
        if (hasDismissedPopover === 'n') {
          shouldShowCoachMark = false;
        }
        // If popover has been dismissed (hasDismissedPopover === 'y'), coachmark becomes independent
        else if (hasDismissedPopover === 'y') {
          shouldShowCoachMark = hasDismissedCoachMarkWeb !== 'y';
        }
      } else {
        shouldShowCoachMark = hasDismissedCoachMarkWeb !== 'y';
      }

      setShowCoachMarkCondition(shouldShowCoachMark);
    }
  }, [
    isSessionStorageReady,
    enableSmartChoiceIntroPopup,
    recommendationPreferencesStatus,
  ]);

  useEffect(() => {
    if (isSmartChoice && recommendationPreferencesStatus) {
      setRecommendationPreferences(recommendationPreferencesStatus);
    }
  }, [recommendationPreferencesStatus, isSmartChoice]);

  // Dispatch event when preferences are loaded from API (for parent app integration)
  useEffect(() => {
    if (
      isParcelWidget &&
      preferencesData?.data?.recommendationPreferencesStatus
    ) {
      dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, {
        preferences: preferencesData.data.recommendationPreferencesStatus,
        source: 'api-fetch',
        timestamp: new Date().toISOString(),
        userId: effectiveUserId,
        isSuperUser,
      });
    }
  }, [
    preferencesData?.data?.recommendationPreferencesStatus,
    isParcelWidget,
    effectiveUserId,
    isSuperUser,
  ]);

  const onClickSaveAndUpdate = (data) => {
    // Build uuid with superUser suffix if applicable (matches expected API format)
    const uuidWithSuffix = effectiveUserId
      ? `${effectiveUserId}${isSuperUser ? '-superUser' : ''}`
      : undefined;

    saveRecommendationPreferences({
      variables: {
        ...data,
        ...(uuidWithSuffix && { uuid: uuidWithSuffix }),
        ...(effectiveUserId && { userId: effectiveUserId }),
      },
    });
    // Note: The save response will be handled by the useEffect that watches saveResult
    // This updates the Zustand store and dispatches events for parent apps
  };

  const updatePreferencesForFirstTimeUser = () => {
    const payload = formatPreferencePayload({
      ...recommendationPreferencesStatus,
      hasDismissedCoachMarkWeb: 'y',
      isSuperUser,
    });

    onClickSaveAndUpdate(payload);
  };
  const ENABLE_NON_SC_DISCLAIMER = enableNonSmartChoiceDisclaimer
    ? isSmartChoiceFFEnabled
    : isSmartChoice;

  const isValidCategoryWithSectionType = isValidCareCategoryWithSectionType(
    chipValue,
    sectionType
  );

  const onClickSmartChoiceHandler = () => {
    const currentChip = chipValue;
    if (
      currentChip === CareCategories?.VIRTUAL_VISIT ||
      currentChip === CareCategories?.ALL ||
      ((currentChip === CareCategories?.PRIMARY_CARE ||
        currentChip === CareCategories?.MEDICAL_SPECIALISTS) &&
        (sectionType?.toLowerCase() === SectionType.PROVIDER.toLowerCase() ||
          sectionType === undefined))
    ) {
      if (recommendationPreferencesStatus?.hasSetPreferences === 'y') {
        setPreferencesModalOpen(true);
        adobeModalTrackEvent('Set preferences');
      } else {
        if (recommendationPreferencesStatus?.hasDismissedCoachMarkWeb !== 'y') {
          updatePreferencesForFirstTimeUser();
        }
        adobeModalTrackEvent('A better way to find providers');
        setShowOnboardingForm(true);
      }
    } else if (enableNonSmartChoiceDisclaimer) {
      setShowNonSmartChoiceOnboarding(true);
    }
  };

  useEffect(() => {
    if (
      recommendationPreferencesStatus?.hasDismissedCoachMarkWeb !== 'y' &&
      showCoachMarkCondition
    ) {
      setCoachmarkWebOpen(true);
    } else {
      setCoachmarkWebOpen(false);
    }
  }, [
    recommendationPreferencesStatus?.hasDismissedCoachMarkWeb,
    setCoachmarkWebOpen,
    showCoachMarkCondition,
  ]);

  useEffect(() => {
    if (coachmarkWeb) {
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
      }, 0);
    }
  }, [coachmarkWeb]);

  const onCloseCoachmarkWeb = () => {
    updatePreferencesForFirstTimeUser();
    setCoachmarkWebOpen(false);
    adobeLinkTrackEvent({
      name: 'close',
      location: 'Smart choice preferences coachmark',
    });
  };

  const checkMobileScreen = (mobileVal: string | null, desktopVal: string) =>
    isMobileScreen ? mobileVal : desktopVal;

  const dismissPopover = async (hasDismissedCoachMarkWeb?: boolean) => {
    setShowSmartchoiceIntro(false);
    sessionStorage.setItem('smartChoicePopoverDismissed', 'true');

    const uuidWithSuffix = effectiveUserId
      ? `${effectiveUserId}${isSuperUser ? '-superUser' : ''}`
      : undefined;

    // When IntroModal closes via handleCloseDrawer, it might be passing an event object.
    // Preserve existing coachmark state if not explicitly provided
    // Only update if explicitly set to true or false
    const coachmarkWebPayload =
      hasDismissedCoachMarkWeb === true
        ? { hasDismissedCoachMarkWeb: 'y' }
        : {};

    const payload = {
      ...recommendationPreferencesStatus,
      hasDismissedPopover: 'y',
      ...coachmarkWebPayload,
      isSuperUser,
      ...(uuidWithSuffix && { uuid: uuidWithSuffix }),
      ...(effectiveUserId && { userId: effectiveUserId }),
    };
    try {
      await saveRecommendationPreferences({
        variables: payload,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error dismissing popover:', error);
    }
    adobeLinkTrackEvent({
      name: 'close',
      location: locationAnalytics,
    });
  };

  const handleLearnAboutSC = async () => {
    await dismissPopover(true);
    adobeLinkTrackEvent({
      name: SMART_CHOICE_INTRO_POP_UP_LEARN_ABOUT?.message,
      location: locationAnalytics,
    });
    if (recommendationPreferencesStatus?.hasSetPreferences === 'y') {
      setPreferencesModalOpen(true);
    } else {
      setShowOnboardingForm(true);
    }
  };

  const handleMaybeLater = async () => {
    await dismissPopover();
    adobeLinkTrackEvent({
      name: SMART_CHOICE_INTRO_POP_UP_MAY_BE_LATER?.message,
      location: locationAnalytics,
    });
  };

  if (
    isParcelWidget &&
    isSessionStorageReady !== undefined &&
    !isSessionStorageReady
  ) {
    return <Skeleton height={20} variant="rounded" width={100} />;
  }

  return (
    <React.Fragment>
      {ENABLE_NON_SC_DISCLAIMER && !isFullScreenCompareSmartChoice && (
        <Flex css={flexCss as any}>
          {ENABLE_NON_SC_DISCLAIMER && !recommendationPreferencesStatus && (
            <Skeleton height={20} variant="rounded" width={100} />
          )}
          {ENABLE_NON_SC_DISCLAIMER &&
            isValidCategoryWithSectionType &&
            recommendationPreferencesStatus && (
              <>
                {showCoachMarkCondition && (
                  <Popover
                    align="center"
                    content={
                      <PopoverContent
                        SC_COACHMARK={SC_COACHMARK}
                        onCloseCoachmarkWeb={onCloseCoachmarkWeb}
                      />
                    }
                    // cast to any because coachmarkStyle returns nested selectors and '!important' values
                    css={
                      {
                        ...{
                          zIndex: '99999',
                          '&.abyss-popover-trigger': {
                            marginTop: '8px',
                          },
                        },
                        ...coachmarkStyle(checkMobileScreen),
                      } as any
                    }
                    data-testid="smart-choice-coachmark-web"
                    open={
                      coachmarkWeb && shouldDisplayCoachMarkBasedOnChipValue()
                    }
                    position={isMobileScreen ? 'left' : 'bottom'}
                    showClose={false}
                    style={{ zIndex: 9999 }}
                    width={450}
                  >
                    <div css={{ display: 'flex', alignItems: 'center' } as any}>
                      <IconSymbol
                        color="$primary1"
                        icon="settings"
                        size={24}
                        variant="outlined"
                      />
                    </div>
                  </Popover>
                )}
                <LinkContainer
                  analyticsInfo={{
                    name: 'smart choice gear icon',
                    location: 'smart choice',
                  }}
                  before={
                    recommendationPreferencesStatus?.hasDismissedCoachMarkWeb ===
                    'y' ? (
                      <IconSymbol
                        color="$primary1"
                        icon="settings"
                        size={24}
                        variant="outlined"
                      />
                    ) : null
                  }
                  css={
                    {
                      ...smartChoiceLinkStyle(isMobileScreen, search),
                    } as any
                  }
                  data-auto-testid="smart-choice-button"
                  data-testid="smart-choice-button"
                  icon={
                    recommendationPreferencesStatus?.hasSetPreferences !== 'y'
                      ? 'circle'
                      : ''
                  }
                  iconSize="18"
                  onClick={() => onClickSmartChoiceHandler()}
                  size={18}
                  text={checkMobileScreen(null, t('SMART_CHOICE.SMART_CHOICE'))}
                />
              </>
            )}
        </Flex>
      )}
      {showNonSmartChoiceOnboarding && (
        <NonSmartChoiceOnboarding
          handleCloseDrawer={() => setShowNonSmartChoiceOnboarding(false)}
          handleOpenSetPreferencesModal={() => setPreferencesModalOpen(true)}
          isOpen={showNonSmartChoiceOnboarding}
        />
      )}
      {isSmartChoiceFFEnabled &&
        enableSmartChoiceIntroPopup &&
        showSmartchoiceIntro && (
          <IntroModal
            handleCloseDrawer={dismissPopover}
            handleLearnAboutSC={handleLearnAboutSC}
            handleMaybeLater={handleMaybeLater}
            isOpen={showSmartchoiceIntro}
          />
        )}
      {showOnboardingForm && (
        <OnboardingForm
          handleCloseDrawer={() => setShowOnboardingForm(false)}
          handleOpenSetPreferencesModal={() => setPreferencesModalOpen(true)}
          isOpen={showOnboardingForm}
        />
      )}
      {isPreferencesModalOpen && (
        <PreferencesForm
          handleCloseDrawer={() => setPreferencesModalOpen(false)}
          isOpen={isPreferencesModalOpen}
          isSuperUser={isSuperUser}
          onClickSaveAndUpdate={onClickSaveAndUpdate}
          preferencesData={recommendationPreferencesStatus}
          preferencesFormFromProviderDetail={false}
          submitButtonLabel={submitButtonLabel}
        />
      )}
    </React.Fragment>
  );
};
